import { ChangeDetectorRef, Component, Inject, NgZone } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { first, last, find, pull } from 'lodash-es';
import kcSdkConfig from '../assets/sdk/kc-sdk-config.json';
import { StringService } from './string.service';
import { SignInService } from './sign-in/sign-in.service';
import { IApisForWindows, IData, IWorkflow } from './sdk-tester.types';
import { SdkTesterService } from './sdk-tester.service';
import kcDevApis from '../../../assets/kc-dev-apis/kc-dev-apis.json';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { SignInComponent } from './sign-in/sign-in.component';

export interface IDevApis {
    name: string;
    sdkVisible: boolean;
    example: object;
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, SignInComponent]
})
export class AppComponent {
    title = 'sdk-tester';
    kcSdk = (window as any).kcSdk;
    kcNotLaunched = false;
    devApis = kcDevApis as IDevApis[];
    apiMethods!: IDevApis[]; // to be populated with the selected api methods
    // to test client specific tokens you need
    // 1. their token
    // 2. their cer key
    // 3. updated appesttings for issuer, audience and cert path
    selectedApi = '';
    SignInService!: SignInService;
    SdkTesterService!: SdkTesterService;
    spacesToIndent = 4;
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private sdkTesterService: SdkTesterService,
        private stringService: StringService,
        private signInService: SignInService,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        this.SignInService = this.signInService;
        this.SdkTesterService = this.sdkTesterService;
        this.kcSdk.setCallback(this.kcSdk.callbacks.SDKEXCEPTION, this.kcWindowDataChanged);
        this.kcSdk.setCallback(this.kcSdk.callbacks.WORKFLOWCOMPLETE, this.workflowComplete);
        this.kcSdk.setCallback(this.kcSdk.callbacks.SDKRESPONSE, this.kcWindowDataChanged);
        this.kcSdk.setCallback(this.kcSdk.callbacks.KCLISTENINGCHANGED, this.kcListeningChanged);
        this.kcSdk.setCallback(this.kcSdk.callbacks.ENCODERLISTENINGCHANGED, this.encoderListeningChanged);
        this.kcSdk.clearWorkflow();
        const authData = {
            grant_type: 'org_login',
            username: 'sdkTester',
            organizationId: 'Justice League',
            enableEncoder: true,
            client_id: kcSdkConfig.appName,
            client_secret: kcSdkConfig.secret
        };
        this.sdkTesterService.filterSessionTypes(this.sdkTesterService.selectedI9I10State);
        this.sdkTesterService.setAuthToken(authData).subscribe();
    }

    populateSdkApis(): void {
        this.apiMethods = this.devApis.filter(api => api.sdkVisible).sort((a, b) => a.name.localeCompare(b.name));
        this.selectedApi = 'launch KC';
    }

    clearInputOutput = () => {
        this.sdkTesterService.outputJson = '';
        this.clearWorkflow();
        this.signInService.clearClientError();
    };

    clearOutput = () => {
        this.sdkTesterService.outputJson = '';
    };

    kcWindowDataChanged = (data: IData) => {
        setTimeout(() => {
            this.sdkTesterService.logOutput(data.callback, data.value);
            this.cdr.detectChanges();
        });
    };

    encoderWindowDataChanged = (data: IData) => {
        setTimeout(() => {
            this.sdkTesterService.logOutput(data.callback, data.value);
            this.cdr.detectChanges();
        });
    };

    kcListeningChanged = (data: IData) => {
        setTimeout(() => {
            this.sdkTesterService.sdkKcIsListening = this.kcSdk.kcWindowIsListening;
            this.sdkTesterService.logOutput(data.callback, this.kcSdk.kcWindowIsListening);
            this.cdr.detectChanges();
        });
    };

    encoderListeningChanged = (data: IData) => {
        setTimeout(() => {
            this.sdkTesterService.sdkEncoderIsListening = this.kcSdk.encoderWindowIsListening;
            this.sdkTesterService.logOutput(data.callback, this.kcSdk.encoderWindowIsListening);
            this.cdr.detectChanges();
        });
    };

    workflowComplete = () => {
        setTimeout(() => {
            this.onSelectedNewWorkflowApiChange();
            this.cdr.detectChanges();
        });
    };

    advancedModeOnChange = () => {
        this.signInService.clearClientError();
        this.clearInputOutput();

        if (this.signInService.advancedMode) {
            this.populateSdkApis();
            this.onSelectedNewWorkflowApiChange();
        }
    };

    clearWorkflow = () => {
        this.kcSdk.clearWorkflow();
    };

    onSelectedWorkflowApiChange = () => {
        const selectedIndex = (this.document.getElementById('kc-workflow') as HTMLSelectElement)?.selectedIndex || 0;

        const selectedWf = this.kcSdk.workflow[selectedIndex];
        if (selectedWf) {
            this.sdkTesterService.workflowInput = JSON.stringify(selectedWf.args, null, 2);
        }
    };

    onSelectedNewWorkflowApiChange = () => {
        if (this.sdkTesterService.clientExampleToken) {
            this.sdkTesterService.exampleAuthToken.access_token = this.sdkTesterService.clientExampleToken;
        }

        let values = this.apiMethods.find(api => api.name === this.selectedApi)?.example;
        switch (this.selectedApi) {
            case 'assignConfig':
                this.sdkTesterService.workflowInput = JSON.stringify({ rcpEndpointUri: this.sdkTesterService.exampleUrl }, null, 2);
                break;
            case 'setDemographics':
                this.sdkTesterService.workflowInput = JSON.stringify(this.sdkTesterService.exampleDemographicsData, null, 2);
                break;
            case 'setAuthToken':
                this.sdkTesterService.workflowInput = JSON.stringify(this.sdkTesterService.exampleAuthToken, null, 2);
                break;
            case 'launch KC':
                this.sdkTesterService.workflowWindow = this.sdkTesterService.kcWindowName;
                const kcURL = kcSdkConfig.url + this.sdkTesterService.WEB_KC;
                const kcState = this.sdkTesterService.selectedI9I10State;
                const kcArgs = [
                    JSON.stringify(kcURL, null, 2),
                    JSON.stringify(this.sdkTesterService.exampleAuthToken, null, 2),
                    JSON.stringify(kcState, null, 2),
                    JSON.stringify(this.sdkTesterService.exampleDemographicsData, null, 2)
                ].join(',\n');
                this.sdkTesterService.workflowInput = this.stringService.wrapAsArray(kcArgs);
                break;
            case 'launch Encoder':
                this.sdkTesterService.workflowWindow = this.sdkTesterService.encoderWindowName;
                const encoderURL = kcSdkConfig.url + this.sdkTesterService.WEB_ENCODER;
                const demographics = this.sdkTesterService.exampleDemographicsData;
                const encoderRequest = {
                    sessionType: this.kcSdk.sessionTypes.I10CM,
                    encoderTab: this.kcSdk.encoderTabs.LOGIC,
                    codePhrase: 'ANGINA'
                };
                const state = this.sdkTesterService.selectedI9I10State;
                const args = [
                    JSON.stringify(encoderURL, null, 2),
                    JSON.stringify(this.sdkTesterService.exampleAuthToken, null, 2),
                    JSON.stringify(state, null, 2),
                    JSON.stringify(demographics, null, 2),
                    JSON.stringify(encoderRequest, null, 2)
                ].join(',\n');
                this.sdkTesterService.workflowInput = this.stringService.wrapAsArray(args);
                break;
            case 'updateCodes':
                this.sdkTesterService.workflowInput = JSON.stringify({ codes: values }, undefined, this.spacesToIndent);
                break;
            case 'getRowSpecificInfo':
                this.sdkTesterService.workflowInput = JSON.stringify({ uniqueIds: values }, undefined, this.spacesToIndent);
                break;
            default:
                this.sdkTesterService.workflowInput = JSON.stringify(values, undefined, this.spacesToIndent);
                break;
        }
        const windowName =
            this.selectedApi === 'launch Encoder' || this.selectedApi === 'launch KC'
                ? this.sdkTesterService.workflowWindow
                : this.kcSdk.kcWindowIsListening
                ? this.sdkTesterService.kcWindowName
                : this.sdkTesterService.encoderWindowName;

        const apiSelected = find(this.sdkTesterService.availableApis, { name: this.selectedApi, windowName });

        if (apiSelected) {
            this.sdkTesterService.availableApiSelected = apiSelected;
        }
        this.kcNotLaunched = !apiSelected;
    };

    dateParser = (key: string, value: Date | string) => {
        if (typeof value === 'string') {
            const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;

            if (reISO.test(value)) {
                return new Date(value);
            }
        }
        return value as Date;
    };

    callApi = () => {
        const args = this.sdkTesterService.workflowInput
            ? this.stringService.isJson(this.sdkTesterService.workflowInput)
                ? JSON.parse(this.sdkTesterService.workflowInput, this.dateParser)
                : this.sdkTesterService.workflowInput
            : [];
        this.kcSdk.clearWorkflow();

        if (this.kcNotLaunched) {
            console.log(new Error('KC window not listening'));
            return;
        }

        this.kcSdk.addToWorkflow({
            fn: this.sdkTesterService.availableApiSelected?.fn,
            args: args
        });
        this.sdkTesterService.selectedWorkflowApi = last(this.kcSdk.workflow);
        this.SdkTesterService.runWorkflow(this.SdkTesterService.workflowWindow);
    };
}
